import { Fragment } from 'preact';
import { Size } from '../utils/UNSAFE_size';
import { Skeleton } from '../UNSAFE_Skeleton';
import { LOADMORE_STYLE_CLASS } from '../PRIVATE_LoadMoreCollection';

// these are arbitrary values by now, might update them with definite values in the future
const DEFAULT_SKELETON_NUMBER = 25;
const DEFAULT_SKELETON_WIDTH = '60x';
const DEFAULT_SKELETON_HEIGHT = '70x';

type SkeletonProps = {
  layout: 'flex' | 'grid';
  colCount: number;
  totalCount: number;
  cardWidth?: number;
  cardHeight?: number;
};

/**
 * A function that returns an array of skeletons based on count
 * @param count the number of skeletons
 */
const getSkeletonsByCount = (
  layout: 'flex' | 'grid',
  count = DEFAULT_SKELETON_NUMBER,
  width?: Size,
  height?: Size,
  isUnique = true
) => {
  const sw = width || (layout === 'flex' ? DEFAULT_SKELETON_WIDTH : '100%');
  const sh = height || DEFAULT_SKELETON_HEIGHT;

  return [...Array(count)].map((_v, i) => (
    // create unique keys if needed, otherwise the animations won't start at the same time, because the old copy of skeletons
    // could be reused, even the key is specified by useId(), combine count with time to make the key unique.
    <Skeleton key={isUnique && `${i}${new Date().getTime()}`} width={sw} height={sh} />
  ));
};

/**
 * When there is no data (i.e. data is an empty array or null, and hasMore is false), we will show 25 card skeletons with default size
 */
export const InitialLoadSkeletons = (props: { layout: SkeletonProps['layout'] }) => (
  <Fragment>{getSkeletonsByCount(props.layout)}</Fragment>
);

/**
 * When we are fetching the initial data (i.e. data is an empty array, and hasMore is true), we will show 25 card skeletons with default size,
 * because we don't know the card dimension at this time. Since there is more data to load, we will specify LOADMORE_STYLE_CLASS on skeletons
 */
export const DefaultLoadMoreSkeletons = (props: { layout: SkeletonProps['layout'] }) => {
  const firstSkeleton = (
    <div class={LOADMORE_STYLE_CLASS}>{getSkeletonsByCount(props.layout, 1)}</div>
  );
  const otherSkeletons = getSkeletonsByCount(props.layout, DEFAULT_SKELETON_NUMBER - 1);
  const skeletons = [firstSkeleton, ...otherSkeletons];
  return <Fragment>{skeletons}</Fragment>;
};

/**
 * During the render that we measure and update card dimension, we will hide skeletons temporarily
 * to avoid showing incorrect skeletons
 */
const InvisibleSkeletons = () => {
  return <div class={LOADMORE_STYLE_CLASS}></div>;
};

export const LoadMoreSkeletons = (props: SkeletonProps) => {
  if (props.colCount < 1 || props.cardWidth == null || props.cardHeight == null) {
    // if column count / card width / card height are invalid, we will show the invisible skeletons
    return <InvisibleSkeletons />;
  } else {
    // after initial renders, show skeletons with correct dimension and count
    const skeletons = [];

    const firstRowColCount = props.colCount - (props.totalCount % props.colCount);
    const isOnlyOneRow = firstRowColCount === props.colCount;

    const cardWidth = `calc(${props.cardWidth}px)` as Size;
    const cardHeight = props.cardHeight
      ? (`calc(${props.cardHeight}px)` as Size)
      : DEFAULT_SKELETON_HEIGHT;

    const width: Size = props.layout === 'flex' ? cardWidth : '100%';
    const height: Size = props.layout === 'flex' || isOnlyOneRow ? cardHeight : '100%';

    // first row
    const firstSkeleton = (
      <div class={LOADMORE_STYLE_CLASS}>{getSkeletonsByCount(props.layout, 1, width, height)}</div>
    );
    skeletons.push(firstSkeleton);
    skeletons.push(getSkeletonsByCount(props.layout, firstRowColCount - 1, width, height));

    // second row
    if (!isOnlyOneRow) {
      skeletons.push(getSkeletonsByCount(props.layout, props.colCount, width, cardHeight));
    }

    return <Fragment>{skeletons}</Fragment>;
  }
};
